import { useState } from "react";
import { ArrowRight, Mail, Phone } from "lucide-react";

export default function Contact() {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [message, setMessage] = useState("");

  const mailto = () => {
    const subject = encodeURIComponent(
      `[Lunipin Pilot Inquiry] ${name || "Anonymous"}${company ? " · " + company : ""}`
    );
    const body = encodeURIComponent(
      `Name: ${name}\nCompany: ${company}\nRole: ${role}\n\n${message}\n\n— Sent from lunipin landing site`
    );
    return `mailto:sanjaykidiyoor@lunipin.com?subject=${subject}&body=${body}`;
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 lg:py-36 bg-black"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#dc2626] mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-[#dc2626]" /> 10 · Open Channel
            </div>
            <h2 className="font-display uppercase font-black text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
              The Hunt
              <br />
              begins <span className="text-[#dc2626]">now.</span>
            </h2>
            <p className="mt-6 text-neutral-400 leading-relaxed max-w-md">
              Tell us about your organization and the behaviours you want to see
              differently. We reply within 48 hours with a briefing.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href="mailto:sanjaykidiyoor@lunipin.com"
                data-testid="contact-email-link"
                className="group flex items-start gap-4 border border-white/10 p-5 hover:border-[#dc2626]/60 transition-colors"
              >
                <Mail className="mt-0.5 text-[#dc2626]" size={20} />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                    Direct Line · Email
                  </div>
                  <div className="mt-1 text-white font-medium">
                    sanjaykidiyoor@lunipin.com
                  </div>
                </div>
                <ArrowRight
                  className="ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                  size={18}
                />
              </a>
              <a
                href="tel:+919845739307"
                data-testid="contact-phone-link"
                className="group flex items-start gap-4 border border-white/10 p-5 hover:border-[#dc2626]/60 transition-colors"
              >
                <Phone className="mt-0.5 text-[#dc2626]" size={20} />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                    Direct Line · Voice
                  </div>
                  <div className="mt-1 text-white font-medium">+91 98457 39307</div>
                </div>
                <ArrowRight
                  className="ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                  size={18}
                />
              </a>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                window.location.href = mailto();
              }}
              data-testid="contact-form"
              className="border border-white/10 bg-[#0a0a0a] p-6 md:p-8 space-y-6"
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#f59e0b]">
                  TRANSMISSION FORM · lunipin.sh
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-[#dc2626]" />
                  <span className="w-2 h-2 bg-[#f59e0b]" />
                  <span className="w-2 h-2 bg-neutral-600" />
                </div>
              </div>

              <Field label="Name">
                <input
                  data-testid="contact-input-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full bg-transparent border-b border-white/10 focus:border-[#dc2626] outline-none py-2 text-white placeholder:text-neutral-600 font-mono"
                />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Company">
                  <input
                    data-testid="contact-input-company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Acme Corp"
                    className="w-full bg-transparent border-b border-white/10 focus:border-[#dc2626] outline-none py-2 text-white placeholder:text-neutral-600 font-mono"
                  />
                </Field>
                <Field label="Role / Network">
                  <input
                    data-testid="contact-input-role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="CISO · L&D Head · HR"
                    className="w-full bg-transparent border-b border-white/10 focus:border-[#dc2626] outline-none py-2 text-white placeholder:text-neutral-600 font-mono"
                  />
                </Field>
              </div>

              <Field label="Message">
                <textarea
                  data-testid="contact-input-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  placeholder="Tell us about the behaviour you want to see under pressure..."
                  className="w-full bg-transparent border-b border-white/10 focus:border-[#dc2626] outline-none py-2 text-white placeholder:text-neutral-600 font-mono resize-none"
                />
              </Field>

              <div className="flex items-center justify-between pt-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600">
                  Encrypted · Signed
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-3 bg-[#dc2626] hover:bg-[#991b1b] px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] font-semibold transition-all hover:translate-y-[-1px] hover:shadow-[0_10px_24px_rgba(220,38,38,0.4)]"
                >
                  Transmit
                  <ArrowRight size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
